import { useQuery } from "@apollo/client";
import {
  faExternalLinkAlt,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GET_INBOX_NOTES from "@graphql/queries/GetInboxNotes.js";
import { GET_PENDING_USER_TEAM_INVITES } from "@graphql/queries/GetUserTeamInvites.js";
import { Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import NoteList from "./NoteList.js";
import TeamInvite from "./TeamInvite.js";
import {
  useLocalStorage,
  usePreventRecordingInterruptions,
  useWatchUserWsEvent,
} from "../../../hooks";
import { useRecordingJobType } from "../../../hooks";
import { useTeam } from "../../../hooks/use-team.js";
import Sort from "../../common/inbox/Sort.js";
import NoteFilters from "../../inbox/NoteFilters.js";

export function DashboardInbox() {
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortAscending, setSortAscending] = useState(false);

  const navigate = useNavigate();
  const { handleActionWithPrevention } =
    usePreventRecordingInterruptions();
  const {
    currentTeam,
    selectedTeamMembers,
    setSelectedTeamMembers,
    refreshCurrentTeam,
  } = useTeam();

  const [selectedRange, _] = useLocalStorage("selected-date-range", {
    startDate: null,
    endDate: null,
    key: "selection",
  });

  const { filterJobType, setFilterJobType } = useRecordingJobType();
  const [queryOptions, setQueryOptions] = useState({
    filter: "unapproved",
    limit: 25,
    offset: 0,
    teamUuid: currentTeam?.uuid,
    startDate: selectedRange.startDate,
    endDate: selectedRange.endDate,
    sortAscending: false,
    jobType: filterJobType?.value,
  });

  const { data, refetch, loading, fetchMore } = useQuery(
    GET_INBOX_NOTES,
    {
      variables: queryOptions,
      fetchPolicy: "network-only",
    },
  );
  useWatchUserWsEvent({
    onMessage: () => refetch(queryOptions),
    onConnect: () => refetch(queryOptions),
    eventName: "note_update",
  });

  const { data: teamInviteData, refetch: refetchTeamInvites } =
    useQuery(GET_PENDING_USER_TEAM_INVITES, {
      fetchPolicy: "network-only",
    });

  useWatchUserWsEvent({
    onMessage: refetchTeamInvites,
    eventName: "team_invite",
  });

  // Handlers

  useEffect(() => {
    if (selectedTeamMembers) {
      setQueryOptions((prev) => ({
        ...prev,
        userUuids: selectedTeamMembers,
      }));
    }
  }, [currentTeam, selectedTeamMembers]);

  useEffect(() => {
    setQueryOptions((prev) => ({
      ...prev,
      teamUuid: currentTeam?.uuid,
    }));
  }, [currentTeam]);

  useEffect(() => {
    refetch(queryOptions);
  }, [queryOptions]);

  function incrementOffset() {
    setLoadingMore(true);

    fetchMore({
      variables: {
        offset: data?.inboxNotes?.notes?.length || 0,
      },
    }).then(() => {
      setLoadingMore(false);
    });
  }

  const navigateToInbox = () => {
    navigate("/inbox");
  };

  const handleSort = async () => {
    const newSortAscending = !sortAscending;
    setSortAscending(newSortAscending);

    setQueryOptions((prev) => ({
      ...prev,
      sortAscending: newSortAscending,
    }));
  };

  return (
    <div className="relative border border-gray-100 bg-white dark:bg-gray-800 dark:border-gray-900/40 rounded-xl shadow-sm">
      <div className="flex flex-col w-full rounded-xl bg-indigo-50 dark:bg-gray-700/50 border-b border-indigo-100 dark:border-gray-800 p-4 shadow-sm">
        <div className="flex flex-row items-center justify-between flex-1">
          <h3 className="text-gray-700">
            Inbox{" "}
            <Tooltip
              arrow
              placement="right"
              title="This is the number of notes in your current Inbox view (according to any filters applied)."
            >
              <span
                className="text-gray-500 text-base"
                data-testid="inbox-note-count"
              >
                {data?.inboxNotes?.totalNotes}
              </span>
            </Tooltip>
          </h3>
          <button
            className="rounded-full shadow-md bg-indigo-500  self-center py-1.5 px-3 text-white font-medium hover:bg-indigo-600 focus:outline-none transition-all"
            onClick={() => {
              handleActionWithPrevention(navigateToInbox);
            }}
          >
            <FontAwesomeIcon
              icon={faExternalLinkAlt}
              size="sm"
              className="mr-1"
            />{" "}
            Expand
          </button>
        </div>

        <div className="flex flex-row flex-wrap gap-1 items-center justify-between space-x-1 mt-2 border-t border-gray-200 dark:border-gray-800 pt-2">
          <div className="flex flex-row items-center space-x-1">
            <FontAwesomeIcon
              icon={faFilter}
              className="text-gray-500"
            />
            <NoteFilters
              setQueryOptions={setQueryOptions}
              currentTeam={currentTeam}
              selectedTeamMembers={selectedTeamMembers}
              setSelectedTeamMembers={setSelectedTeamMembers}
              selectedJobType={filterJobType}
              setSelectedJobType={setFilterJobType}
              refreshCurrentTeam={refreshCurrentTeam}
              filter="unapproved"
            />
          </div>
          <div className="flex flex-row items-center space-x-1">
            <Sort sortAscending={sortAscending} onSort={handleSort} />
          </div>
        </div>
      </div>

      <div className="flex flex-col items-start rounded-b-lg pt-2 px-2 w-full">
        {teamInviteData?.pendingUserTeamInvites?.length > 0 && (
          <div className="w-full px-[2%] flex flex-col gap-2">
            {teamInviteData?.pendingUserTeamInvites.map((invite) => (
              <TeamInvite key={invite.uuid} invite={invite} />
            ))}
          </div>
        )}
        <div className="relative flex flex-row items-center justify-end w-full">
          <div className="z-10 bg-gradient-to-b from-white via-white dark:from-gray-800 dark:via-gray-800 absolute top-0 w-full flex justify-center pt-5 px-5"></div>
        </div>

        <div className="h-full relative w-full">
          <NoteList
            totalNotes={data?.inboxNotes?.totalNotes || 0}
            items={data?.inboxNotes?.notes || []}
            refetch={() => refetch(queryOptions)}
            incrementOffset={incrementOffset}
            loadingMore={loadingMore}
            loading={loading}
            handleActionWithPrevention={handleActionWithPrevention}
            dataTestId="dashboard-note-list"
          />
        </div>
      </div>
    </div>
  );
}
